import { useState } from 'react'
import { Download, FileDown, Loader2, Sheet } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { clientService } from '@/services/client-service'
import { ClientFilters } from '@/types/client'

import { getErrorMessage } from '../utils/error.utils'
interface ClientExportProps {
  filters?: ClientFilters
  totalClients?: number
}

export function ClientExport({ filters = {}, totalClients = 0 }: ClientExportProps) {
  const [exporting, setExporting] = useState<'csv' | 'excel' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      setExporting(format)
      setError(null)

      const blob = await clientService.exportClients(filters, format)
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Set filename with current date
      const date = new Date().toISOString().split('T')[0]
      const filename = `clientes_${date}.${format === 'excel' ? 'xlsx' : 'csv'}`
      link.download = filename
      
      // Trigger download
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up
      window.URL.revokeObjectURL(url)
      
    } catch (err: unknown) {
      setError(getErrorMessage(err, `Erro ao exportar arquivo ${format.toUpperCase()}`))
      console.error('Export error:', err)
    } finally {
      setExporting(null)
    }
  }

  const formatFilters = () => {
    const activeFilters: string[] = []
    
    if (filters.search) {
      activeFilters.push(`Busca: "${filters.search}"`)
    }
    
    if (filters.clientType) {
      const typeLabel = filters.clientType === 'PERSON' ? 'Pessoa Física' : 'Pessoa Jurídica'
      activeFilters.push(`Tipo: ${typeLabel}`)
    }
    
    if (filters.status) {
      const statusLabel = filters.status === 'ACTIVE' ? 'Ativo' : 'Inativo'
      activeFilters.push(`Status: ${statusLabel}`)
    }
    
    if (filters.tags && filters.tags.length > 0) {
      activeFilters.push(`Tags: ${filters.tags.join(', ')}`)
    }
    
    if (filters.dateFrom || filters.dateTo) {
      const dateRange: string[] = []
      if (filters.dateFrom) dateRange.push(`desde ${filters.dateFrom}`)
      if (filters.dateTo) dateRange.push(`até ${filters.dateTo}`)
      activeFilters.push(`Período: ${dateRange.join(' ')}`)
    }
    
    if (filters.hasEmail !== undefined) {
      activeFilters.push(`Com email: ${filters.hasEmail ? 'Sim' : 'Não'}`)
    }
    
    if (filters.hasPhone !== undefined) {
      activeFilters.push(`Com telefone: ${filters.hasPhone ? 'Sim' : 'Não'}`)
    }
    
    if (filters.city) {
      activeFilters.push(`Cidade: ${filters.city}`)
    }
    
    if (filters.state) {
      activeFilters.push(`Estado: ${filters.state}`)
    }

    return activeFilters
  }

  const activeFilters = formatFilters()

  const exportButton = (format: 'csv' | 'excel', Icon: typeof Sheet, title: string, subtitle: string) => (
    <button
      type="button"
      onClick={() => handleExport(format)}
      disabled={exporting !== null}
      className="flex items-center justify-center rounded-lg border border-dashed px-4 py-4 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {exporting === format ? (
        <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Exportando {format === 'csv' ? 'CSV' : 'Excel'}...
        </span>
      ) : (
        <span className="text-center">
          <Icon className="mx-auto size-7 text-muted-foreground" aria-hidden="true" />
          <span className="mt-2 block text-sm font-medium">{title}</span>
          <span className="block text-xs text-muted-foreground">{subtitle}</span>
        </span>
      )}
    </button>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="size-4 text-muted-foreground" aria-hidden="true" />
          <h2>Exportar Dados dos Clientes</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error && (
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
        )}

        <div className="flex items-start gap-3 rounded-md border bg-muted/40 p-3">
          <FileDown className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div>
            <h4 className="text-sm font-medium">Informações da Exportação</h4>
            <div className="mt-1 text-sm text-muted-foreground">
              <p>• Total de clientes: <strong className="text-foreground">{totalClients}</strong></p>
              <p>• Formatos disponíveis: CSV e Excel (XLSX)</p>
              <p>• Inclui dados pessoais, empresariais e de contato</p>
            </div>
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="rounded-md border p-3">
            <h4 className="mb-2 text-sm font-medium">Filtros Ativos (serão aplicados na exportação):</h4>
            <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
              {activeFilters.map((filter, index) => (
                <li key={index} className="flex items-center gap-2">
                  <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
                  {filter}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {exportButton('csv', Sheet, 'Exportar CSV', 'Arquivo de texto separado por vírgulas')}
          {exportButton('excel', FileDown, 'Exportar Excel', 'Planilha do Microsoft Excel (.xlsx)')}
        </div>

        <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
          <p className="mb-1 font-medium">Colunas incluídas na exportação:</p>
          <p>
            Número do cliente, tipo, status, nome/razão social, CPF/CNPJ,
            emails, telefones, endereços, tags, observações, data de criação,
            última atualização.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
